import { IAddChargebreakdownCsvDataAfterMapping } from "../interface/AddChargebreakdownTypes";
import {
  dateValidity,
  isPositiveNumber,
  maxChargebreakdownNumberCharacters,
  maxLengthValidity,
  numberValidityIfValueExist,
  stringValidity as stringValidityIfValueExist,
} from "../utils/AddChargebreakdownUtils";
import { AddChargebreakdownItemCommon, FormatOptions } from "./AddChargebreakdownItem.common";

interface IChargebreakdownDataProps {
  chargebreakdownData: IAddChargebreakdownCsvDataAfterMapping;
  chargebreakdownIndex: number;
  formatOptions?: FormatOptions;
}

export class AddChargebreakdownItem extends AddChargebreakdownItemCommon {
  public readonly rawChargebreakdownData: IAddChargebreakdownCsvDataAfterMapping;
  private readonly chargebreakdownIndex: number;
  private readonly dateFormatForPersistence: string = "MM/DD/YYYY";

  constructor(chargebreakdownDataProps: IChargebreakdownDataProps) {
    super(chargebreakdownDataProps.formatOptions);
    const { chargebreakdownData, chargebreakdownIndex } = chargebreakdownDataProps;
    this.rawChargebreakdownData = chargebreakdownData;
    this.chargebreakdownIndex = chargebreakdownIndex;
  }

  get key() {
    return "chargebreakdown-" + this.chargebreakdownIndex;
  }

  get upcItemNumber() {
    return this.rawChargebreakdownData.upcItemNumber;
  }

  get upcItemNumberErrors(): string[] | null {
    return stringValidityIfValueExist(this.rawChargebreakdownData.upcItemNumber ?? "", "UPC");
  }

  get itemNumber() {
    return this.rawChargebreakdownData.itemNumber;
  }

  get itemNumberErrors(): string[] | null {
    return stringValidityIfValueExist(this.rawChargebreakdownData.itemNumber ?? "", "Item Number");
  }

  get poDate() {
    return this.rawChargebreakdownData.poDate ? this.formatDate(this.rawChargebreakdownData.poDate, this.dateFormatForPersistence) : null;
  }

  get poDateFormatted(): string | null {
    return this.rawChargebreakdownData.poDate ? this.formatDate(this.rawChargebreakdownData.poDate) : null;
  }

  get poDateErrors(): string[] | null {
    return dateValidity(this.rawChargebreakdownData.poDate ?? "", "PO Date", false);
  }

  get quantity() {
    return this.rawChargebreakdownData.quantity ?? null;
  }

  get quantityFormatted(): string | null {
    return this.rawChargebreakdownData.quantity ? this.formatNumber(Number(this.rawChargebreakdownData.quantity), "twoDecimal") : null;
  }

  get quantityErrors(): string[] | null {
    return numberValidityIfValueExist(this.rawChargebreakdownData.quantity ?? "", "Quantity");
  }

  get allowance() {
    if (typeof this.rawChargebreakdownData.allowance === "number") {
      return this.formatNumber(Number(this.rawChargebreakdownData.allowance), "twoDecimal");
    }
    return this.rawChargebreakdownData.allowance;
  }

  get allowanceErrors(): string[] | null {
    return stringValidityIfValueExist(this.rawChargebreakdownData.allowance ?? "", "Allowance");
  }

  get packSize() {
    return this.rawChargebreakdownData.packSize;
  }

  get packSizeErrors(): string[] | null {
    return stringValidityIfValueExist(this.rawChargebreakdownData.packSize ?? "", "Pack Size");
  }

  get poNumber() {
    return this.rawChargebreakdownData.poNumber;
  }

  get poNumberErrors(): string[] | null {
    return stringValidityIfValueExist(this.rawChargebreakdownData.poNumber ?? "", "PO Number");
  }

  get shipDate() {
    return this.rawChargebreakdownData.shipDate ? this.formatDate(this.rawChargebreakdownData.shipDate, this.dateFormatForPersistence) : null;
  }

  get shipDateFormatted(): string | null {
    return this.rawChargebreakdownData.shipDate ? this.formatDate(this.rawChargebreakdownData.shipDate) : null;
  }

  get shipDateErrors(): string[] | null {
    return dateValidity(this.rawChargebreakdownData.shipDate ?? "", "Ship Date", false);
  }

  get unitCost() {
    return this.rawChargebreakdownData.unitCost ?? null;
  }

  get unitCostFormatted(): string | null {
    return this.rawChargebreakdownData.unitCost ? this.formatNumber(Number(this.rawChargebreakdownData.unitCost), "twoDecimal") : null;
  }

  get unitCostErrors(): string[] | null {
    return numberValidityIfValueExist(this.rawChargebreakdownData.unitCost ?? "", "Unit Cost");
  }

  get expiryDate() {
    return this.rawChargebreakdownData.expiryDate ? this.formatDate(this.rawChargebreakdownData.expiryDate, this.dateFormatForPersistence) : null;
  }

  get expiryDateFormatted(): string | null {
    return this.rawChargebreakdownData.expiryDate ? this.formatDate(this.rawChargebreakdownData.expiryDate) : null;
  }

  get expiryDateErrors(): string[] | null {
    return dateValidity(this.rawChargebreakdownData.expiryDate ?? "", "Expiry Date", false);
  }

  get refInvoice() {
    return this.rawChargebreakdownData.refInvoice;
  }

  get refInvoiceErrors(): string[] | null {
    return stringValidityIfValueExist(this.rawChargebreakdownData.refInvoice ?? "", "Ref. Invoice");
  }

  get invoiceDate() {
    return this.rawChargebreakdownData.invoiceDate ? this.formatDate(this.rawChargebreakdownData.invoiceDate, this.dateFormatForPersistence) : null;
  }

  get invoiceDateFormatted(): string | null {
    return this.rawChargebreakdownData.invoiceDate ? this.formatDate(this.rawChargebreakdownData.invoiceDate) : null;
  }

  get invoiceDateErrors(): string[] | null {
    return dateValidity(this.rawChargebreakdownData.invoiceDate ?? "", "Invoice Date", false);
  }

  get totalAmount() {
    return this.rawChargebreakdownData.totalAmount ?? null;
  }

  get totalAmountFormatted(): string | null {
    return this.rawChargebreakdownData.totalAmount ? this.formatNumber(Number(this.rawChargebreakdownData.totalAmount), "twoDecimal") : null;
  }

  get totalAmountErrors(): string[] | null {
    if (this.rawChargebreakdownData.totalAmount === null || this.rawChargebreakdownData.totalAmount === undefined) {
      return ["Total Amount is required."];
    }
    if (typeof this.rawChargebreakdownData.totalAmount === "string") {
      return ["Total Amount must be a number."];
    }
    if (!isPositiveNumber(this.totalAmount)) {
      return ["Total Amount must be greater than 0."];
    }

    return null;
  }

  get receivedDate() {
    return this.rawChargebreakdownData.receivedDate ? this.formatDate(this.rawChargebreakdownData.receivedDate, this.dateFormatForPersistence) : null;
  }

  get receivedDateFormatted(): string | null {
    return this.rawChargebreakdownData.receivedDate ? this.formatDate(this.rawChargebreakdownData.receivedDate) : null;
  }

  get receivedDateErrors(): string[] | null {
    return dateValidity(this.rawChargebreakdownData.receivedDate ?? "", "Received Date", false);
  }

  get retailerName() {
    return this.rawChargebreakdownData.retailerName;
  }

  get retailerNameErrors(): string[] | null {
    return maxLengthValidity(this.rawChargebreakdownData.retailerName ?? "", maxChargebreakdownNumberCharacters, "Retailer Name");
  }

  get invoiceAmount() {
    return this.rawChargebreakdownData.invoiceAmount ?? null;
  }

  get invoiceAmountFormatted(): string | null {
    return this.rawChargebreakdownData.invoiceAmount ? this.formatNumber(Number(this.rawChargebreakdownData.invoiceAmount), "twoDecimal") : null;
  }

  get invoiceAmountErrors(): string[] | null {
    return numberValidityIfValueExist(this.rawChargebreakdownData.invoiceAmount ?? "", "Invoice Amount");
  }

  get orderQuantity() {
    return this.rawChargebreakdownData.orderQuantity ?? null;
  }

  get orderQuantityFormatted(): string | null {
    return this.rawChargebreakdownData.orderQuantity ? this.formatNumber(Number(this.rawChargebreakdownData.orderQuantity), "twoDecimal") : null;
  }

  get orderQuantityErrors(): string[] | null {
    return numberValidityIfValueExist(this.rawChargebreakdownData.orderQuantity ?? "", "Order Quantity");
  }

  get costDifference() {
    return this.rawChargebreakdownData.costDifference ?? null;
  }

  get costDifferenceFormatted(): string | null {
    return this.rawChargebreakdownData.costDifference ? this.formatNumber(Number(this.rawChargebreakdownData.costDifference), "twoDecimal") : null;
  }

  get costDifferenceErrors(): string[] | null {
    return numberValidityIfValueExist(this.rawChargebreakdownData.costDifference ?? "", "Cost Difference");
  }

  get retailerNumber() {
    return this.rawChargebreakdownData.retailerNumber;
  }

  get retailerNumberErrors(): string[] | null {
    return stringValidityIfValueExist(this.rawChargebreakdownData.retailerNumber ?? "", "Retailer Number");
  }

  get itemDescription() {
    return this.rawChargebreakdownData.itemDescription;
  }

  get itemDescriptionErrors(): string[] | null {
    return maxLengthValidity(this.rawChargebreakdownData.itemDescription ?? "", maxChargebreakdownNumberCharacters, "Item Description");
  }

  get receivedUnitCost() {
    return this.rawChargebreakdownData.receivedUnitCost ?? null;
  }

  get receivedUnitCostFormatted(): string | null {
    return this.rawChargebreakdownData.receivedUnitCost ? this.formatNumber(Number(this.rawChargebreakdownData.receivedUnitCost), "twoDecimal") : null;
  }

  get receivedUnitCostErrors(): string[] | null {
    return numberValidityIfValueExist(this.rawChargebreakdownData.receivedUnitCost ?? "", "Received Unit Cost");
  }

  get distributionCenter() {
    return this.rawChargebreakdownData.distributionCenter;
  }

  get distributionCenterErrors(): string[] | null {
    return stringValidityIfValueExist(this.rawChargebreakdownData.distributionCenter ?? "", "Distribution Center");
  }

  get quantityDifference() {
    return this.rawChargebreakdownData.quantityDifference ?? null;
  }

  get quantityDifferenceFormatted(): string | null {
    return this.rawChargebreakdownData.quantityDifference ? this.formatNumber(Number(this.rawChargebreakdownData.quantityDifference), "twoDecimal") : null;
  }

  get quantityDifferenceErrors(): string[] | null {
    return numberValidityIfValueExist(this.rawChargebreakdownData.quantityDifference ?? "", "Quantity Difference");
  }

  get discount() {
    if (typeof this.rawChargebreakdownData.discount === "number") {
      return this.formatNumber(Number(this.rawChargebreakdownData.discount), "twoDecimal");
    }
    return this.rawChargebreakdownData.discount;
  }

  get discountErrors(): string[] | null {
    return stringValidityIfValueExist(this.rawChargebreakdownData.discount ?? "", "Discount");
  }

  get divisionName() {
    return this.rawChargebreakdownData.divisionName;
  }

  get divisionNameErrors(): string[] | null {
    return maxLengthValidity(this.rawChargebreakdownData.divisionName ?? "", maxChargebreakdownNumberCharacters, "Division Name");
  }

  get divisionNumber() {
    return this.rawChargebreakdownData.divisionNumber;
  }

  get divisionNumberErrors(): string[] | null {
    return stringValidityIfValueExist(this.rawChargebreakdownData.divisionNumber ?? "", "Division Number");
  }

  get originalChargebreakdownData(): IAddChargebreakdownCsvDataAfterMapping {
    return this.rawChargebreakdownData;
  }

  get hasErrors(): boolean {
    return (
      (this.upcItemNumberErrors?.length ?? 0) > 0 ||
      (this.itemNumberErrors?.length ?? 0) > 0 ||
      (this.poDateErrors?.length ?? 0) > 0 ||
      (this.quantityErrors?.length ?? 0) > 0 ||
      (this.shipDateErrors?.length ?? 0) > 0 ||
      (this.unitCostErrors?.length ?? 0) > 0 ||
      (this.expiryDateErrors?.length ?? 0) > 0 ||
      (this.invoiceDateErrors?.length ?? 0) > 0 ||
      (this.totalAmountErrors?.length ?? 0) > 0 ||
      (this.receivedDateErrors?.length ?? 0) > 0 ||
      (this.invoiceAmountErrors?.length ?? 0) > 0 ||
      (this.orderQuantityErrors?.length ?? 0) > 0 ||
      (this.costDifferenceErrors?.length ?? 0) > 0 ||
      (this.receivedUnitCostErrors?.length ?? 0) > 0 ||
      (this.quantityDifferenceErrors?.length ?? 0) > 0 ||
      (this.retailerNumberErrors?.length ?? 0) > 0 ||
      (this.itemDescriptionErrors?.length ?? 0) > 0 ||
      (this.distributionCenterErrors?.length ?? 0) > 0 ||
      (this.divisionNameErrors?.length ?? 0) > 0 ||
      (this.divisionNumberErrors?.length ?? 0) > 0
    );
  }

  public static toJson(chargebreakdownData: IAddChargebreakdownCsvDataAfterMapping): string {
    return JSON.stringify(chargebreakdownData);
  }
}
